import React, { useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Brush,
  ReferenceLine,
} from 'recharts';
import { Box, Tooltip as MuiTooltip } from '@mui/material';
import { displayCFDState, taskArnMapState } from 'features/cfd/state/cfdAtoms';
import { displayStudyState } from 'features/optimization/state/studyAtoms';
import { hoveredOptCFDidState, selectedOptCFDState } from 'features/optimization/state/trialAtoms';
import { authTokenState } from 'features/auth/state/authAtoms';
import {
  createDataList,
  createHypervolumeDataList,
  CustomTooltipContent,
  summarizeFailedOnly,
} from 'features/chart/components/CustomTooltip';
import { errorCodesState } from 'features/error-codes/state/errorCodesAtoms';
import { lookupErrorMessage } from 'features/error-codes/utils/lookupErrorMessage';
import { fetchZipFromS3 } from 'features/cfd/api/fetchZipFromS3';
import { OBJECTIVE_PRESETS } from 'shared/domain/objectivePresets';
import { GlassEmptyState } from 'shared/ui/GlassEmptyState';

const customSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
};

export const ChartPanel: React.FC = () => {
  const [taskArnMap, setTaskArnMap] = useRecoilState(taskArnMapState);
  const hoveredCFDid = useRecoilValue(hoveredOptCFDidState);
  const selectedOptCFD = useRecoilValue(selectedOptCFDState);
  const displayStudy = useRecoilValue(displayStudyState);
  const displayCFD = useRecoilValue(displayCFDState);
  const authToken = useRecoilValue(authTokenState);
  const dataList = createDataList(displayStudy);
  const hvDataList = createHypervolumeDataList(displayStudy);
  const failedOnlySummary = useMemo(() => summarizeFailedOnly(displayStudy), [displayStudy]);
  const errorCodesDict = useRecoilValue(errorCodesState);

  // 多目的最適化かどうか（hypervolume_list が存在する場合）
  const isMultiObjective = hvDataList.length > 0;

  const studyPreset = displayStudy ? OBJECTIVE_PRESETS[displayStudy.presetId] : undefined;
  const studySecondaryMeta = studyPreset?.objectives[1];
  const hasStudySecondary = Boolean(studySecondaryMeta);

  const selectedOptObjectiveKeys = selectedOptCFD
    ? OBJECTIVE_PRESETS[selectedOptCFD.presetId].objectives.map((o) => o.key)
    : [];
  const primaryObjectiveKey = selectedOptObjectiveKeys[0] ?? 'cd_0deg';
  const secondaryObjectiveKey = selectedOptObjectiveKeys[1];

  // 第1目的用 Y軸ドメイン
  const yDomain1 = useMemo(() => {
    let minY: number = Infinity;
    let maxY: number = -Infinity;

    if (dataList && dataList.length > 0) {
      dataList.forEach((d) => {
        if (d.value_1 !== null && !isNaN(Number(d.value_1))) {
          const v = Number(d.value_1);
          minY = Math.min(minY, v);
          maxY = Math.max(maxY, v);
        }
      });
    }

    if (minY === Infinity || maxY === -Infinity) {
      minY = 0;
      maxY = 1;
    }

    // displayCFD の第1目的も考慮
    if (displayCFD) {
      const firstKey = OBJECTIVE_PRESETS[displayCFD.presetId].objectives[0]?.key;
      if (firstKey) {
        const val = displayCFD.value[firstKey];
        if (!isNaN(val)) {
          minY = Math.min(minY, val);
          maxY = Math.max(maxY, val);
        }
      }
      minY -= 2e-3;
      maxY += 2e-3;
    }

    return [minY, maxY];
  }, [dataList, displayCFD]);

  // 第2目的用 Y軸ドメイン（多目的時のみ使用）
  const yDomain2 = useMemo(() => {
    let minY: number = Infinity;
    let maxY: number = -Infinity;

    if (dataList && dataList.length > 0) {
      dataList.forEach((d) => {
        if (d.value_2 !== null && !isNaN(Number(d.value_2))) {
          const v = Number(d.value_2);
          minY = Math.min(minY, v);
          maxY = Math.max(maxY, v);
        }
      });
    }

    if (minY === Infinity || maxY === -Infinity) {
      minY = 0;
      maxY = 1;
    }

    // displayCFD の第2目的も考慮
    if (displayCFD) {
      const secondKey = OBJECTIVE_PRESETS[displayCFD.presetId].objectives[1]?.key;
      if (secondKey) {
        const val = displayCFD.value[secondKey];
        if (!isNaN(val)) {
          minY = Math.min(minY, val);
          maxY = Math.max(maxY, val);
        }
      }
      minY -= 2e-3;
      maxY += 2e-3;
    }

    return [minY, maxY];
  }, [dataList, displayCFD]);

  // 変更1: クリックハンドラを ID で受ける
  const handleDotClickById = async (cfd_id?: string) => {
    if (!cfd_id || authToken === '') return;
    const taskArn = taskArnMap[cfd_id];
    const arn = await fetchZipFromS3(authToken, cfd_id, taskArn);
    setTaskArnMap((prev) => ({ ...prev, [cfd_id]: arn }));
  };

  const makeDotRenderer = (cdKey: 'value_1' | 'value_2', idKey: 'cfd_id_1' | 'cfd_id_2') =>
    ({ cx, cy, payload }: { cx: number; cy: number; payload: any }): React.ReactElement<SVGElement> => {
      const cdVal = payload?.[cdKey];
      const cfd_id = payload?.[idKey] as string | undefined;

      const selectedId =
        cdKey === 'value_1'
          ? selectedOptCFD?.cfd_id?.[primaryObjectiveKey]
          : (secondaryObjectiveKey ? selectedOptCFD?.cfd_id?.[secondaryObjectiveKey] : undefined);

      const isHighlighted =
        (!!hoveredCFDid && cfd_id === hoveredCFDid) ||
        (!!selectedId && cfd_id === selectedId);

      // ★ ここで常に一意・安定な key を作る（trial 等の軸値＋cdKey＋cfd_id）
      const keyStr = `dot-${cdKey}-${cfd_id ?? 'none'}-${String(payload?.trial ?? payload?.index ?? '')}`;

      // 値が欠損なら空の <g> を "key 付き" で返す
      if (cdVal === null || cdVal === undefined || isNaN(Number(cdVal))) {
        return <g key={keyStr} />;
      }

      return (
        <g
          key={keyStr}
          onClick={(e) => { e.stopPropagation(); handleDotClickById(cfd_id); }}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
        >
          <circle cx={cx} cy={cy} r={12} fill="rgba(0,0,0,0)" pointerEvents="all" />
          <circle
            cx={cx}
            cy={cy}
            r={6}
            fill={isHighlighted ? '#00c853' : '#ffffff'}
            stroke="#888"
            pointerEvents="none"
          />
        </g>
      );
    };

  if (!displayCFD && (!displayStudy || dataList.length === 0)) {
    return (
      <Box component="div" sx={customSx}>
        <GlassEmptyState message="No data to display graphically." />
      </Box>
    );
  }

  return (
    <div style={{ width: '100%', height: '99%', display: 'flex', flexDirection: 'column' }}>
      {/* 上部: 目的関数チャート（多目的時は70%、単目的時は100%） */}
      <div style={{ width: '100%', height: isMultiObjective ? '70%' : '100%', position: 'relative' }}>
        {failedOnlySummary && (
          <MuiTooltip
            arrow
            placement="right-start"
            title={
              <Box component="div" sx={{ maxHeight: 240, overflowY: 'auto' }}>
                {failedOnlySummary.codes.map(({ trial, code }) => {
                  const lookup = lookupErrorMessage(code, errorCodesDict);
                  const text = lookup ? lookup.message ?? `不明なエラー (${lookup.code})` : code;
                  return (
                    <div key={trial} style={{ fontSize: 12, lineHeight: 1.6 }}>
                      Trial {trial}: {text}
                    </div>
                  );
                })}
              </Box>
            }
          >
            <Box
              component="div"
              sx={{
                position: 'absolute',
                top: 8,
                left: 64,
                zIndex: 10,
                background: '#ffebee',
                border: '1px solid #d32f2f',
                borderRadius: '4px',
                padding: '4px 10px',
                color: '#b71c1c',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'help',
                userSelect: 'none',
              }}
            >
              失敗のみ {failedOnlySummary.count}件
            </Box>
          </MuiTooltip>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={dataList} syncId={isMultiObjective ? 'chartSync' : undefined}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="trial" />
            {/* 左Y軸: 第1目的 */}
            <YAxis
              yAxisId="left"
              orientation="left"
              domain={yDomain1}
              tickFormatter={(value) => value.toFixed(3)}
              stroke="#1976d2"
            />
            {/* 右Y軸: 第2目的（多目的時のみ） */}
            {isMultiObjective && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={yDomain2}
                tickFormatter={(value) => value.toFixed(3)}
                stroke="#4caf50"
              />
            )}
            <Tooltip content={<CustomTooltipContent />} />

            {displayStudy && dataList.length !== 0 && (
              (studyPreset?.objectives || []).map((meta, idx) => {
                if (idx === 1 && !hasStudySecondary) return null;
                const suffix = idx === 0 ? '_1' : '_2';
                const minKey = `min${suffix}`;
                const cdKey = `value${suffix}` as 'value_1' | 'value_2';
                const cfd_idKey = `cfd_id${suffix}` as 'cfd_id_1' | 'cfd_id_2';
                const minStroke = idx === 0 ? '#ff4040' : '#ff9900';
                const cdStroke = idx === 0 ? '#1976d2' : '#4caf50';
                // 多目的時: idx=0 は左軸、idx=1 は右軸
                const axisId = isMultiObjective ? (idx === 0 ? 'left' : 'right') : 'left';
                return (
                  <React.Fragment key={`study-${meta.key}`}>
                    <Line
                      key={`line-min-${meta.key}`}
                      type="monotone"
                      dataKey={minKey}
                      stroke={minStroke}
                      dot={false}
                      name={`Min (${meta.label})`}
                      yAxisId={axisId}
                    />
                    <Line
                      key={`line-cd-${meta.key}`}
                      type="monotone"
                      dataKey={cdKey}
                      stroke={cdStroke}
                      dot={makeDotRenderer(cdKey, cfd_idKey)}
                      name={`Cd (${meta.label})`}
                      yAxisId={axisId}
                    />
                  </React.Fragment>
                );
              })
            )}

            {/* 失敗 trial の × マーカー（current min 上に重ねる）。
                エラーコード単位は 1 系列のため、左軸（第一目的軸）に固定。 */}
            {displayStudy && dataList.length !== 0 && (
              <Line
                key="line-failed-1"
                type="monotone"
                dataKey="failed_value_1"
                stroke="transparent"
                connectNulls={false}
                isAnimationActive={false}
                legendType="none"
                name="Failed trial"
                yAxisId="left"
                dot={({ cx, cy, payload }: { cx: number; cy: number; payload: any }) => {
                  const failedY = payload?.failed_value_1;
                  if (failedY === null || failedY === undefined) {
                    return <g key={`failed-${payload?.trial ?? 'na'}`} />;
                  }
                  const s = 6;
                  return (
                    <g key={`failed-${payload?.trial ?? 'na'}`}>
                      <line x1={cx - s} y1={cy - s} x2={cx + s} y2={cy + s} stroke="#d32f2f" strokeWidth={2} />
                      <line x1={cx - s} y1={cy + s} x2={cx + s} y2={cy - s} stroke="#d32f2f" strokeWidth={2} />
                    </g>
                  );
                }}
                activeDot={false}
              />
            )}

            {displayCFD && (
              OBJECTIVE_PRESETS[displayCFD.presetId].objectives.map((meta, idx) => {
                const val = displayCFD.value[meta.key];
                if (val === undefined || isNaN(val)) return null;
                // 多目的時: idx=0 は左軸、idx=1 は右軸
                const axisId = isMultiObjective ? (idx === 0 ? 'left' : 'right') : 'left';
                return (
                  <ReferenceLine
                    key={`ref-${meta.key}`}
                    y={val}
                    stroke={meta.color}
                    yAxisId={axisId}
                  />
                );
              })
            )}

            {/* 単目的時のみ Brush を上部チャートに配置 */}
            {!isMultiObjective && (
              <Brush
                dataKey="trial"
                height={15}
                stroke="#1976d2"
                travellerWidth={10}
                startIndex={0}
                endIndex={dataList.length - 1}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 下部: Hypervolume チャート（多目的時のみ表示、30%） */}
      {isMultiObjective && (
        <div style={{ width: '100%', height: '30%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={hvDataList} syncId="chartSync">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="trial" />
              <YAxis
                domain={[0, 'auto']}
                tickFormatter={(value) => value.toFixed(2)}
              />
              <Tooltip
                formatter={(value: number) => [value?.toFixed(4) ?? 'N/A', 'Hypervolume']}
              />
              <Line
                type="monotone"
                dataKey="hypervolume"
                stroke="#9c27b0"
                dot={false}
                name="Hypervolume"
                connectNulls={false}
              />
              <Brush
                dataKey="trial"
                height={15}
                stroke="#1976d2"
                travellerWidth={10}
                startIndex={0}
                endIndex={hvDataList.length - 1}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
